import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';

import { FsBadgeModule } from '@firestitch/badge';
import { FsSidenavModule, Item, ItemActionType } from '@firestitch/sidenav';
import { FsStore } from '@firestitch/store';

import { Observable, Subject } from 'rxjs';
import { skip, takeUntil } from 'rxjs/operators';

import { AclPermission } from '@common/enums';
import { Acl, IndicatorService } from '@common/services';

import { IndicatorComponent } from '@libs/indicator';

import { SidenavConfig } from '../../../interfaces';
import { SidenavService } from '../../../services/sidenav.service';

import { EnvironmentMenuComponent } from './environment-menu/environment-menu.component';


@Component({
  selector: 'app-page-side-nav-bottom',
  templateUrl: './side-nav-bottom.component.html',
  styleUrls: ['./side-nav-bottom.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FsSidenavModule,
    AsyncPipe,
    FsBadgeModule,
    IndicatorComponent,
  ],
})
export class SideNavBottomComponent implements OnInit, OnDestroy {

  @ViewChild('system', { read: TemplateRef, static: true })
  public systemTmpl: TemplateRef<any>;

  @ViewChild('systemLogs', { read: TemplateRef, static: true })
  public systemLogsTmpl: TemplateRef<any>;

  public sideNavConfig: SidenavConfig = { items: [] };

  private _store = inject(FsStore);
  private _acl = inject(Acl);
  private _cdRef = inject(ChangeDetectorRef);
  private _sidenavService = inject(SidenavService);
  private _destroy$ = new Subject<void>();
  private _indicatorService = inject(IndicatorService);

  public get unacknowledgedLogCount$(): Observable<number> {
    return this._indicatorService.unacknowledgedLogCount$;
  }

  public ngOnInit(): void {
    this._buildSidenavBottom();

    this._store.observe('permissions')
      .pipe(
        skip(1),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this._buildSidenavBottom();
      });

    this._store.observe('environment')
      .pipe(
        skip(1),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this._buildSidenavBottom();
      });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _buildSidenavBottom(): void {
    const items: Item[] = [
      ...this._getEnvironmentItems(),
      ...this._getAdminItems(),
      ...this._getSystemItems(),
    ];

    this.sideNavConfig = {
      itemClick: ({ item }) => {
        if(item && !(item.items || []).length) {
          this._sidenavService.hideMobile();
        }
      },
      items,
    };

    this._cdRef.markForCheck();
  }

  private _getEnvironmentItems(): Item[] {
    const environment = this._store.get('environment');
    const hasWorkspaceWrite = environment ?
      this._acl.hasWrite(AclPermission.Environment, environment.id) :
      false;

    const item: any = {
      superlabel: 'Workspace',
      label: 'Not Selected',
      exact: true,
      actions: [
        {
          type: ItemActionType.Menu,
          icon: 'more_vert',
          menu: {
            component: EnvironmentMenuComponent,
            data: { environment, hasWorkspaceWrite },
          },
        },
      ],
    };

    if (environment) {
      item.label = environment.name;
      item.image = environment.avatar?.tiny;
      if (this._acl.hasPermissionAdmin() || hasWorkspaceWrite) {
        item.path = `/workspace/${environment.id}`;
        item.items = [
          { label: 'Members', path: `/workspace/${environment.id}/members`, exact: false },
          { label: 'Tasks', path: `/workspace/${environment.id}/tasks`, exact: false },
          { label: 'Docs', path: `/workspace/${environment.id}/doc`, exact: false },
          { label: 'Mockups', path: `/workspace/${environment.id}/images`, exact: false },
          { label: 'Plans', path: `/workspace/${environment.id}/plans`, exact: false },
          { label: 'Projects', path: `/workspace/${environment.id}/projects`, exact: false },
          { label: 'Settings', path: `/workspace/${environment.id}/settings`, exact: false },
        ];
      }
    }

    return [item];
  }

  private _getAdminItems(): Item[] {
    if (!this._acl.hasPermissionAdmin()) {
      return [];
    }

    return [
      {
        label: 'Admin',
        path: '/admin',
        icon: 'settings',
        class: 'admin',
        items: [
          {
            label: 'Accounts',
            path: '/admin/accounts',
            exact: false,
          },
          {
            label: 'Workspaces',
            path: '/admin/workspaces',
            exact: false,
          },
          {
            label: 'Messages',
            path: '/admin/message',
            exact: false,
          },
          {
            label: 'Settings',
            path: '/admin/settings',
            exact: false,
          },
        ],
      },
    ];
  }

  private _getSystemItems(): Item[] {
    if (!this._acl.hasPermissionAdmin()) {
      return [];
    }

    return [
      {
        template: this.systemTmpl,
        path: '/system',
        icon: 'personal_video',
        items: [
          {
            label: 'Settings',
            path: '/system/settings',
          },
          {
            label: 'Crons',
            path: '/system/crons',
          },
          {
            label: 'File Manager',
            path: '/system/files',
          },
          {
            label: 'Processes',
            path: '/system/processes',
          },
          {
            template: this.systemLogsTmpl,
            path: '/system/logs',
          },
          {
            label: 'Info',
            path: '/system/info',
          },
          {
            label: 'Blueprint',
            path: '/system/blueprint',
            exact: false,
          },
        ],
      },
    ];
  }
}
